/** Widget type → body, header title, meta and icon (screenshots/67, 69, 70, 71). */
import type { ComponentType } from "react";
import { entryOf } from "../catalog";
import type { WidgetType } from "../types";
import { BriefMe, Coalitions, OppositionWatch, TargetList, TossUps } from "./advocacy";
import { DataWidget } from "./DataWidget";
import { Allies, ConstituentsSpoke, LobbyingOnItems, MeetingLog, WardSaid, WhipCount } from "./office";
import type { WidgetProps } from "./props";
import { AlderVotes, HowWouldYouVote, NeighborsSpoke, YourAlderperson } from "./resident";
import { CouncilTension, ForecastWatch, LiveNow, MetricNumber, NextVote, Notifications, SinceLastVisit, UpcomingMeetings } from "./shared";

export const BODIES: Record<WidgetType, ComponentType<WidgetProps>> = {
  nextVote: NextVote, sinceLastVisit: SinceLastVisit, forecastWatch: ForecastWatch, councilTension: CouncilTension, upcomingMeetings: UpcomingMeetings,
  liveNow: LiveNow, notifications: Notifications,
  yourAlderperson: YourAlderperson, howWouldYouVote: HowWouldYouVote, alderVotes: AlderVotes, neighborsSpoke: NeighborsSpoke,
  wardSaid: WardSaid, whipCount: WhipCount, constituentsSpoke: ConstituentsSpoke, lobbyingOnItems: LobbyingOnItems, meetingLog: MeetingLog,
  tossUps: TossUps, targetList: TargetList, coalitions: Coalitions, oppositionWatch: OppositionWatch, briefMe: BriefMe,
  chart: DataWidget, table: DataWidget, number: (p) => (p.item.config.metric ? <MetricNumber {...p} /> : <DataWidget {...p} />), allies: Allies,
};

const low = (s?: string) => s?.toLowerCase();

/** The header: icon, title and grey meta, as each screenshot spells them. */
export function header(p: WidgetProps): { title: string; meta?: string; icon: string } {
  const { item, feeds, size, snapshot } = p;
  const e = entryOf(item.type);
  const c = item.config;
  const ward = snapshot?.viewer.ward;
  if (item.type === "number" && c.metric) {
    const m = feeds.metrics?.[c.metric];
    return { title: c.title ?? m?.title ?? e.title, meta: c.meta ?? m?.meta, icon: c.icon ?? m?.icon ?? e.icon };
  }
  if (item.type === "chart" || item.type === "table") {
    // "{alderperson}" → the resident's alderperson's last name (69: "Ald. Sigcho-López fundraising before council meetings").
    const t = (c.title ?? c.settings?.labels.title)?.replace("{alderperson}", feeds.yourAlderperson?.name.split(" ").slice(-1)[0] ?? "");
    const ds = snapshot?.datasets[item.id];
    return { title: t ? (size === "S" ? t : `${e.title} · ${t}`) : e.title, meta: c.meta ?? ds?.datasetId, icon: c.icon ?? e.icon };
  }
  const meta: Partial<Record<WidgetType, string | undefined>> = {
    nextVote: feeds.nextVote ? `in ${feeds.nextVote.inDays} day${feeds.nextVote.inDays === 1 ? "" : "s"}` : undefined,
    tossUps: size === "L" ? low(feeds.tossUps?.item) : undefined,
    forecastWatch: feeds.forecastWatch ? `${feeds.forecastWatch.items.length} items you follow` : undefined,
    targetList: "team",
    oppositionWatch: feeds.oppositionWatch?.meta,
    howWouldYouVote: ward ? `totals go to Ward ${ward}` : undefined,
    sinceLastVisit: feeds.sinceLastVisit?.since,
    whipCount: low(feeds.whipCount?.item),
    wardSaid: feeds.nextVote ? `${feeds.nextVote.date} items` : undefined,
    constituentsSpoke: "this term",
    meetingLog: "team only",
    neighborsSpoke: "public comments",
  };
  const title = c.title
    ?? (item.type === "neighborsSpoke" && feeds.neighborsSpoke ? `Neighbors who spoke from Ward ${feeds.neighborsSpoke.ward}` : undefined)
    ?? (item.type === "wardSaid" && size === "S" && feeds.wardSaid ? `Ward ${feeds.wardSaid.ward} responses` : undefined)
    ?? (item.type === "tossUps" && size !== "S" ? "Toss-ups to work" : undefined)
    ?? e.title;
  const icon = c.icon ?? (item.type === "wardSaid" && size === "S" ? "vote" : item.type === "tossUps" && size === "S" ? "users" : e.icon);
  // "{item}" in a saved meta is the toss-up item's short name from the feed (70: "landlord & tenant"), never hard-coded.
  const own = c.meta?.replace("{item}", feeds.tossUps?.itemShort ?? "") || undefined;
  return { title, meta: own ?? meta[item.type], icon };
}
